import React, { useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { ModalWrapper } from 'shared/modals/v2-wrapper';
import { DesktopOnly, MobileOnly } from 'shared/components';
import useStores from 'shared/hooks/use-stores';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { validateAddressLimits } from 'src/state/actions/address';

import { ModalContainer, BottomDrawer } from 'components/core';
import { withModalControlsHook, ModalComponentProps } from 'components/modals/with-modal-controls-hook';
import { StoreSwitched } from './store-switched';

type StoreSwitchedModalProps = ModalComponentProps<undefined, boolean>;

type UseStoreSwitched = Pick<StoreSwitchedModalProps, 'onClose'>;

type UseStoreSwitchedReturn = {
  dispensaryName?: string;
  isClearing: boolean;
  handleClose: () => void;
  handleClearCart: () => Promise<void>;
};

export function useStoreSwitched({ onClose }: UseStoreSwitched): UseStoreSwitchedReturn {
  const { Cart } = useStores();
  const { dispensary } = useDispensary();
  const apolloClient = useApolloClient();
  const [isClearing, setIsClearing] = useState(false);

  const handleClose = (): void => {
    onClose(false);
  };

  const handleClearCart = async (): Promise<void> => {
    if (isClearing) return;
    setIsClearing(true);

    const hadAddress = Cart.hasAddress;
    const savedAddress = Cart.address;
    Cart.clearOrder();

    if (hadAddress && dispensary) {
      try {
        const result = await validateAddressLimits(Cart, apolloClient, savedAddress, dispensary);
        if (!result.success) {
          Cart.resetAddress();
        }
      } catch {
        Cart.resetAddress();
      }
    }

    onClose(true);
  };

  return {
    dispensaryName: dispensary?.name,
    isClearing,
    handleClose,
    handleClearCart,
  };
}

const StoreSwitchedModal = ({ onClose }: StoreSwitchedModalProps): JSX.Element | null => {
  const { dispensaryName, isClearing, handleClose, handleClearCart } = useStoreSwitched({ onClose });

  return dispensaryName ? (
    <>
      <DesktopOnly mediaQuery='sm'>
        <ModalWrapper open>
          <ModalContainer width='468px'>
            <StoreSwitched
              currentDispensaryName={dispensaryName}
              isClearing={isClearing}
              onClose={handleClose}
              onClearCart={handleClearCart}
            />
          </ModalContainer>
        </ModalWrapper>
      </DesktopOnly>
      <MobileOnly mediaQuery='xs'>
        <BottomDrawer onClose={handleClose} open>
          <StoreSwitched
            currentDispensaryName={dispensaryName}
            isClearing={isClearing}
            onClose={handleClose}
            onClearCart={handleClearCart}
          />
        </BottomDrawer>
      </MobileOnly>
    </>
  ) : null;
};

const [StoreSwitchedController, useStoreSwitchedModal] = withModalControlsHook<undefined, boolean>(StoreSwitchedModal);

export { useStoreSwitchedModal, StoreSwitchedController };
